import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Loader2, LayoutGrid, ShieldAlert, ShieldCheck, Building2, AlertTriangle } from 'lucide-react';
import { HistorialPanel } from './HistorialPanel';
import { BulkSearch } from './ArcaBulkSearch';

const cleanCuit = (v) => (v || '').replace(/\D/g, '');
const fmtFecha = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('es-AR'); } catch { return iso; } };

// Normaliza las actividades declaradas desde el resumen del padrón (RG o monotributo).
const actividadesDe = (r) => {
  const list = (Array.isArray(r?.actividades) ? r.actividades : []).map((a) => ({
    desc: a.descripcion || '', cod: a.id ?? a.codigo ?? '',
    principal: String(a.orden) === '1' || String(a.orden) === '1.0',
  })).filter((a) => a.desc);
  if (list.length) return list;
  if (r?.monotributo?.actividad) return [{ desc: r.monotributo.actividad, cod: r.monotributo.actividad_id ?? '', principal: true }];
  return [];
};

const fetchTodo = async (api, c) => {
  const [p, a] = await Promise.allSettled([api.get(`/arca/consulta/${c}`), api.get(`/arca/apoc/${c}`)]);
  const padron = p.status === 'fulfilled' ? p.value.data : null;
  const padronErr = p.status === 'rejected' ? (p.reason?.response?.data?.detail || 'Error') : '';
  const apoc = a.status === 'fulfilled' ? a.value.data : null;
  const apocErr = a.status === 'rejected' ? (a.reason?.response?.data?.detail || 'Error') : '';
  if (!padron && !apoc) { const e = new Error('Error'); e.response = { data: { detail: padronErr || apocErr || 'Error consultando ARCA' } }; throw e; }
  return { padron, padronErr, apoc, apocErr };
};

const TodoDetalle = ({ data }) => {
  const r = data.padron?.resumen || {};
  const ap = data.apoc?.resumen || {};
  const dom = r.domicilio || {};
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <p className="text-xs font-semibold text-sky-700 mb-1 flex items-center gap-1"><Building2 size={13} /> Padrón</p>
        {data.padron ? (
          <div className="text-sm space-y-0.5">
            <p><span className="text-slate-500">Razón social:</span> {r.razon_social || '—'}</p>
            <p><span className="text-slate-500">Estado:</span> {r.estado || '—'}</p>
            <p><span className="text-slate-500">Condición IVA:</span> {r.condicion_iva || '—'}</p>
            {r.monotributo && <p><span className="text-slate-500">Monotributo:</span> {r.monotributo.categoria}</p>}
            <p><span className="text-slate-500">Domicilio:</span> {[dom.direccion, dom.localidad, dom.provincia].filter(Boolean).join(', ') || '—'}</p>
            {actividadesDe(r).length > 0 && <p><span className="text-slate-500">Actividad:</span> {(actividadesDe(r).find((a) => a.principal) || actividadesDe(r)[0]).desc}</p>}
          </div>
        ) : <p className="text-sm text-amber-700">{data.padronErr}</p>}
      </div>
      <div>
        <p className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1"><ShieldAlert size={13} /> Apócrifos</p>
        {data.apoc ? (
          ap.es_apocrifo ? (
            <div className="space-y-2">
              {(ap.publicaciones || []).map((p, i) => (
                <div key={i} className="text-sm border rounded-lg p-2 bg-white">
                  <p><span className="text-slate-500">Descripción:</span> {p.descripcion || '—'}</p>
                  <p><span className="text-slate-500">Publicación:</span> {fmtFecha(p.fecha_publicacion)}</p>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-emerald-700 flex items-center gap-1"><ShieldCheck size={15} /> Sin observaciones.</p>
        ) : <p className="text-sm text-amber-700">{data.apocErr}</p>}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right break-all">{value ?? '—'}</span>
  </div>
);

export default function ArcaBuscarTodo() {
  const navigate = useNavigate();
  const [cuit, setCuit] = useState('');
  const [loading, setLoading] = useState(false);
  const [padron, setPadron] = useState(null);
  const [padronErr, setPadronErr] = useState('');
  const [apoc, setApoc] = useState(null);
  const [apocErr, setApocErr] = useState('');
  const [buscado, setBuscado] = useState(false);

  const buscar = async (e) => {
    e?.preventDefault();
    const c = cleanCuit(cuit);
    if (c.length !== 11) { toast.error('El CUIT debe tener 11 dígitos'); return; }
    setLoading(true); setBuscado(true);
    setPadron(null); setPadronErr(''); setApoc(null); setApocErr('');
    const [pRes, aRes] = await Promise.allSettled([
      api.get(`/arca/consulta/${c}`),
      api.get(`/arca/apoc/${c}`),
    ]);
    if (pRes.status === 'fulfilled') setPadron(pRes.value.data);
    else setPadronErr(pRes.reason?.response?.data?.detail || 'Error consultando Padrón');
    if (aRes.status === 'fulfilled') setApoc(aRes.value.data);
    else setApocErr(aRes.reason?.response?.data?.detail || 'Error consultando Apócrifos');
    setLoading(false);
  };

  const r = padron?.resumen || {};
  const ap = apoc?.resumen || {};
  const esApocrifo = !!ap.es_apocrifo;
  const needsConfig = (padronErr + apocErr).match(/certificado|clave|CUIT representada|adher/i);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="arca-buscartodo-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><LayoutGrid size={22} /> Buscar todo (Padrón + Apócrifos)</h1>
        <p className="text-slate-500 text-sm mt-1">Ingresá un CUIT y consultá el Padrón y la base de Apócrifos de ARCA en una sola búsqueda.</p>
      </div>

      <Tabs defaultValue="consulta">
        <TabsList data-testid="arca-buscartodo-tabs">
          <TabsTrigger value="consulta" data-testid="arca-buscartodo-tab-consulta">Consulta</TabsTrigger>
          <TabsTrigger value="masivo" data-testid="arca-buscartodo-tab-masivo">Masivo</TabsTrigger>
          <TabsTrigger value="historial" data-testid="arca-buscartodo-tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="consulta" className="space-y-5 mt-4">
          <Card className="p-5">
            <form onSubmit={buscar} className="flex items-center gap-3">
              <Input
                data-testid="arca-buscartodo-cuit-input"
                placeholder="CUIT (11 dígitos)"
                value={cuit}
                onChange={(e) => setCuit(e.target.value)}
                className="max-w-xs font-mono"
                maxLength={13}
              />
              <Button type="submit" disabled={loading} data-testid="arca-buscartodo-btn" className="bg-slate-800 hover:bg-slate-900">
                {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
                {loading ? 'Buscando…' : 'Buscar todo'}
              </Button>
            </form>
          </Card>

          {needsConfig && (
            <Card className="p-4 border-amber-200 bg-amber-50" data-testid="arca-buscartodo-config">
              <div className="flex items-start gap-3 text-amber-800">
                <AlertTriangle size={18} className="mt-0.5" />
                <div>
                  <p className="font-medium">Falta configurar el certificado ARCA.</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/m/arca/configuracion')}>Ir a Configuración</Button>
                </div>
              </div>
            </Card>
          )}

          {/* Banner Apócrifo bien visible */}
          {buscado && apoc && (
            <Card className={`p-4 border-2 ${esApocrifo ? 'border-rose-500 bg-rose-50' : 'border-emerald-300 bg-emerald-50'}`} data-testid="arca-buscartodo-apoc-banner">
              <div className="flex items-center gap-3">
                {esApocrifo ? <ShieldAlert size={26} className="text-rose-600" /> : <ShieldCheck size={26} className="text-emerald-600" />}
                <div>
                  <p className={`font-bold ${esApocrifo ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {esApocrifo ? 'CONTRIBUYENTE APÓCRIFO' : 'CUIT sin observaciones de apócrifos'}
                  </p>
                  {esApocrifo && Array.isArray(ap.publicaciones) && ap.publicaciones.length > 0 && (
                    <p className="text-xs text-rose-600">{ap.publicaciones.length} publicación(es) en la base APOC · última: {fmtFecha(ap.publicaciones[0].fecha_publicacion)}</p>
                  )}
                </div>
                {apoc.cached && <Badge variant="outline" className="ml-auto text-slate-500">caché</Badge>}
              </div>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* Padrón */}
            <Card className="p-5" data-testid="arca-buscartodo-padron">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={18} className="text-sky-600" />
                <h2 className="font-semibold text-slate-900">Padrón</h2>
                {padron?.cached && <Badge variant="outline" className="ml-auto text-slate-500">caché</Badge>}
              </div>
              {padronErr && <p className="text-sm text-amber-700">{padronErr}</p>}
              {padron && (
                <div>
                  <Row label="Razón social" value={r.razon_social} />
                  <Row label="Tipo de persona" value={r.tipo_persona} />
                  <Row label="Estado clave" value={r.estado} />
                  <Row label="Condición IVA" value={r.condicion_iva} />
                  {r.monotributo && <Row label="Monotributo" value={r.monotributo.categoria} />}
                  {r.domicilio && <Row label="Domicilio" value={[r.domicilio.direccion, r.domicilio.localidad, r.domicilio.provincia].filter(Boolean).join(', ')} />}
                  {actividadesDe(r).length > 0 && (
                    <div className="mt-2" data-testid="arca-buscartodo-actividades">
                      <p className="text-sm text-slate-500 mb-1">Actividad declarada</p>
                      <ul className="text-sm text-slate-700 space-y-0.5">
                        {actividadesDe(r).slice(0, 6).map((a, i) => (
                          <li key={i}>{a.principal ? '★ ' : ''}{a.desc} {a.cod ? <span className="text-slate-400">({a.cod})</span> : null}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {!padron && !padronErr && <p className="text-sm text-slate-400">—</p>}
            </Card>

            {/* Apócrifos */}
            <Card className="p-5" data-testid="arca-buscartodo-apoc">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert size={18} className="text-rose-600" />
                <h2 className="font-semibold text-slate-900">Apócrifos (WSAPOC)</h2>
                {apoc?.cached && <Badge variant="outline" className="ml-auto text-slate-500">caché</Badge>}
              </div>
              {apocErr && <p className="text-sm text-amber-700">{apocErr}</p>}
              {apoc && (
                <div>
                  <Row label="¿Es apócrifo?" value={esApocrifo ? 'SÍ' : 'No'} />
                  {esApocrifo && Array.isArray(ap.publicaciones) && ap.publicaciones.map((p, i) => (
                    <div key={i} className="mt-2 text-sm border rounded-lg p-3 bg-slate-50">
                      <p><span className="text-slate-500">Descripción:</span> {p.descripcion || '—'}</p>
                      <p><span className="text-slate-500">Fecha condición:</span> {fmtFecha(p.fecha_condicion)}</p>
                      <p><span className="text-slate-500">Fecha publicación:</span> {fmtFecha(p.fecha_publicacion)}</p>
                    </div>
                  ))}
                </div>
              )}
              {!apoc && !apocErr && <p className="text-sm text-slate-400">—</p>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="masivo" className="mt-4">
          <BulkSearch
            accent="slate"
            fetchOne={(c) => fetchTodo(api, c)}
            summaryHeaders={['Razón social', 'Estado', 'Apócrifo']}
            renderSummaryCells={(d) => {
              const rp = d.padron?.resumen || {}; const ap = d.apoc?.resumen || {};
              return [
                d.padron ? (rp.razon_social || '—') : <span className="text-amber-600 text-xs">sin padrón</span>,
                d.padron ? (rp.estado || '—') : '—',
                d.apoc ? (ap.es_apocrifo ? <Badge className="bg-rose-600 text-white">APÓCRIFO</Badge> : <Badge className="bg-emerald-600 text-white">Sin obs.</Badge>) : <span className="text-amber-600 text-xs">sin apoc</span>,
              ];
            }}
            rowHighlight={(d) => (d.apoc?.resumen?.es_apocrifo ? 'bg-rose-50' : '')}
            renderDetail={(d) => <TodoDetalle data={d} />}
            onConfigError={() => navigate('/m/arca/configuracion')}
            exportConfig={{
              headers: ['CUIT', 'Razón social', 'Tipo persona', 'Estado', 'Condición IVA', 'Monotributo', 'Actividad principal', 'Cód. act. principal', 'Todas las actividades', 'Impuestos', 'Domicilio', 'Localidad', 'Provincia', 'CP', 'Apócrifo', 'Publicaciones', 'Resultado'],
              row: (r) => {
                if (r.status !== 'ok') return [r.cuit, ...Array(15).fill(''), r.status === 'error' ? `ERROR: ${r.error}` : 'sin buscar'];
                const rp = r.data?.padron?.resumen || {}; const ap = r.data?.apoc?.resumen || {}; const pubs = ap.publicaciones || [];
                const dom = rp.domicilio || {};
                const acts = actividadesDe(rp); const pr = acts.find((a) => a.principal) || acts[0] || {};
                const impuestos = (rp.impuestos || []).map((i) => i.descripcion || i.id).join(' | ');
                const todas = acts.map((a) => `${a.desc}${a.cod ? ` (${a.cod})` : ''}`).join(' | ');
                return [r.cuit, rp.razon_social || '', rp.tipo_persona || '', rp.estado || '', rp.condicion_iva || '', rp.monotributo?.categoria || '', pr.desc || '', pr.cod || '', todas, impuestos, dom.direccion || '', dom.localidad || '', dom.provincia || '', dom.codigo_postal || '', r.data?.apoc ? (ap.es_apocrifo ? 'SÍ' : 'NO') : '?', pubs.length, 'OK'];
              },
            }}
          />
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <HistorialPanel showFilter limit={150} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
